/*
 * Publication runs — history of aggregate-domain publish attempts. Each
 * run is a single push of the workspace's domain data to a configured
 * target (DHIS2 / CSV-export / external API). Surfaces the import
 * summary, failures, and a replay action so operators can re-run a
 * failed publication after fixing the upstream issue.
 *
 * Backend: /api/aggregate-publication/runs already returns runs + items;
 * /api/aggregate-publication/replay restarts a specific run.
 */

import { useEffect, useMemo, useState } from "react";
import PageBreadcrumbs from "../../layouts/PageBreadcrumbs.js";
import TableToolbar from "../../components/TableToolbar.js";
import { useTableState } from "../../lib/useTableState.js";
import { api, ApiError } from "../../lib/api.js";

type Run = Awaited<ReturnType<typeof api.aggregatePublicationRuns>>["runs"][number];

export default function AdminPublicationRuns() {
  const [runs, setRuns] = useState<Run[] | null>(null);
  const [profiles, setProfiles] = useState<Map<string, string>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  function refresh() {
    setError(null);
    Promise.all([api.aggregatePublicationRuns(), api.aggregatePublicationProfiles()])
      .then(([rs, ps]) => {
        setRuns(rs.runs);
        const map = new Map<string, string>();
        for (const p of ps.profiles) map.set(p.id, p.name);
        setProfiles(map);
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  }
  useEffect(refresh, []);

  async function replay(runId: string) {
    setBusyId(runId);
    setInfo(null);
    setError(null);
    try {
      const r = await api.replayAggregatePublication(runId);
      setInfo(`Replayed run ${r.run.id} (status: ${r.run.status}). Refresh to see updated status.`);
      setTimeout(refresh, 800);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  }

  const tableRows = useMemo<Array<Run & Record<string, unknown>>>(
    () => (runs ?? []).map((r) => ({
      ...r,
      profileName: profiles.get(r.profileId) ?? r.profileId
    })),
    [runs, profiles]
  );
  const t = useTableState(tableRows, {
    initialPageSize: 25,
    searchFields: ["id", "profileId", "status", "mode"],
    initialSortKey: "startedAt",
    initialSortDir: "desc"
  });

  const stats = useMemo(() => {
    const list = runs ?? [];
    return {
      total: list.length,
      completed: list.filter((r) => r.status === "completed").length,
      failed: list.filter((r) => r.status === "failed").length,
      running: list.filter((r) => r.status === "running").length
    };
  }, [runs]);

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Publication runs</h1>
          <p className="muted">
            History of aggregate-domain publication attempts (push to DHIS2 / CSV / external API).
            Failed runs can be replayed in place — fix the underlying issue (auth, mapping,
            metadata version) first, then click Replay.
          </p>
        </div>
        <div className="actions">
          <button type="button" className="btn btn-secondary" onClick={refresh}>Refresh</button>
        </div>
      </header>

      {error && <div className="ts-alert ts-alert-error">{error}</div>}
      {info && <div className="ts-alert ts-alert-info">{info}</div>}

      <section className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12 }}>
          <Stat label="Total runs" value={stats.total} />
          <Stat label="Completed" value={stats.completed} accent="ok" />
          <Stat label="Running" value={stats.running} accent={stats.running > 0 ? "info" : "muted"} />
          <Stat label="Failed" value={stats.failed} accent={stats.failed > 0 ? "warn" : "muted"} />
        </div>
      </section>

      {runs === null ? (
        <p className="muted">Loading…</p>
      ) : runs.length === 0 ? (
        <div className="empty-state-card">
          <h2 style={{ marginTop: 0 }}>No publication runs yet</h2>
          <p>
            No aggregate-domain publication has been kicked off. Configure a publication target
            (Administration → Publication targets), then trigger a run from the corresponding
            aggregate domain.
          </p>
        </div>
      ) : (
        <>
          <TableToolbar
            search={t.search} setSearch={t.setSearch}
            page={t.page} setPage={t.setPage}
            totalPages={t.totalPages} totalRows={t.totalRows}
            pageSize={t.pageSize} setPageSize={t.setPageSize}
            searchPlaceholder="run / profile / status…"
            exportFilename="publication-runs"
            exportColumns={["id", "profileId", "profileName", "status", "mode", "itemCount", "startedAt", "completedAt", "error"]}
            exportRowsData={t.sorted}
          />
          <table className="transfer-table">
            <thead>
              <tr>
                <th>Run</th><th>Profile</th><th>Mode</th><th>Status</th>
                <th style={{ textAlign: "right" }}>Items</th><th>Started</th><th>Completed</th><th />
              </tr>
            </thead>
            <tbody>
              {(t.paged as unknown as Array<Run & { profileName?: string }>).map((r) => (
                <tr key={r.id}>
                  <td className="mono" style={{ fontSize: 11 }}>{r.id}</td>
                  <td>{r.profileName ?? r.profileId}</td>
                  <td>{r.mode ?? "publish"}</td>
                  <td>
                    <span className={`pill pill-${r.status === "completed" ? "ok" : r.status === "failed" ? "err" : r.status === "running" ? "info" : "muted"}`}>
                      {r.status}
                    </span>
                  </td>
                  <td style={{ textAlign: "right" }}>{r.itemCount}</td>
                  <td style={{ fontSize: 11 }}>{new Date(r.startedAt).toLocaleString()}</td>
                  <td style={{ fontSize: 11 }}>{r.completedAt ? new Date(r.completedAt).toLocaleString() : "—"}</td>
                  <td style={{ textAlign: "right" }}>
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => void replay(r.id)}
                      disabled={busyId === r.id || r.status === "running"}
                      title="Re-execute this publication"
                    >
                      {busyId === r.id ? "Replaying…" : "Replay"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: number; accent?: "ok" | "warn" | "muted" | "info" }) {
  const color =
    accent === "ok" ? "#065f46"
    : accent === "warn" ? "#92400e"
    : accent === "info" ? "#1e3a8a"
    : accent === "muted" ? "#475569"
    : "#1e293b";
  const bg =
    accent === "ok" ? "#d1fae5"
    : accent === "warn" ? "#fef3c7"
    : accent === "info" ? "#dbeafe"
    : "#f1f5f9";
  return (
    <div style={{ background: bg, color, padding: "10px 12px", borderRadius: 8 }}>
      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.4, opacity: 0.7 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 600, marginTop: 4 }}>{value.toLocaleString()}</div>
    </div>
  );
}
